import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { GroupAvailabilityPoll, PollResponse } from '@/types/poll';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef } from 'react';

interface ActivePollWithGroup extends GroupAvailabilityPoll {
  group_name: string;
  group_avatar_url?: string;
}

const POLLS_QUERY_KEY = 'user-active-polls';

export const useUserActivePolls = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const invalidatePolls = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    }, 400);
  };

  const { data: polls = [], isLoading: loading, error: queryError } = useQuery<ActivePollWithGroup[]>({
    queryKey: [POLLS_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user) return [];

      // 1. Get all group IDs where user is a member (with admin flag)
      const { data: userGroups, error: groupsError } = await supabase
        .from('group_members')
        .select('group_id, is_admin, groups(name)')
        .eq('user_id', user.id);

      if (groupsError) throw groupsError;
      if (!userGroups || userGroups.length === 0) return [];

      const groupIds = userGroups.map(g => g.group_id);
      const adminGroupIds = new Set(
        userGroups.filter(g => g.is_admin).map(g => g.group_id)
      );

      // 2. Get active polls from user's groups
      const { data: pollsData, error: pollsError } = await supabase
        .from('group_availability_polls')
        .select(`
          *,
          groups (name, avatar_url),
          padel_clubs(id, club_name, street_address)
        `)
        .in('group_id', groupIds)
        .eq('status', 'open')
        .gt('registration_deadline', new Date().toISOString())
        .order('registration_deadline', { ascending: true });

      if (pollsError) throw pollsError;
      if (!pollsData || pollsData.length === 0) return [];

      const pollIds = pollsData.map(p => p.id);

      // 3. Batch-fetch ALL responses for ALL polls in a single query
      const { data: allResponses } = await supabase
        .from('group_poll_responses')
        .select('poll_id, user_id, response')
        .in('poll_id', pollIds);

      // 4. Batch-fetch profiles for all respondents in one query
      const respondentIds = [...new Set((allResponses || []).map(r => r.user_id))];
      let profileMap: Record<string, { full_name: string; avatar_url: string | null }> = {};

      if (respondentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', respondentIds);

        (profiles || []).forEach(p => {
          profileMap[p.id] = { full_name: p.full_name || 'Unknown', avatar_url: p.avatar_url || null };
        });
      }

      // 5. Assemble polls with pre-fetched data
      return pollsData.map((poll: any) => {
        const pollResponses = (allResponses || []).filter(r => r.poll_id === poll.id);
        const responseCount = pollResponses.length;
        const availableCount = pollResponses.filter(r => r.response === 'available').length;
        const userResponse = pollResponses.find(r => r.user_id === user.id);

        const respondents = pollResponses.map(r => ({
          user_id: r.user_id,
          response: r.response,
          full_name: profileMap[r.user_id]?.full_name ?? 'Unknown',
          avatar_url: profileMap[r.user_id]?.avatar_url ?? null,
        }));

        return {
          ...poll,
          group_name: poll.groups?.name || 'Unknown Group',
          group_avatar_url: poll.groups?.avatar_url,
          response_count: responseCount,
          available_count: availableCount,
          user_response: userResponse?.response as PollResponse,
          is_user_admin: adminGroupIds.has(poll.group_id),
          respondents,
          club: poll.padel_clubs ? {
            id: poll.padel_clubs.id,
            club_name: poll.padel_clubs.club_name,
            street_address: poll.padel_clubs.street_address,
          } : undefined,
        } as ActivePollWithGroup;
      });
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
  });

  // Real-time subscription scoped to user's groups via debounced invalidation
  useEffect(() => {
    if (!user) return;

    const channelName = `user-active-polls-${user.id}-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_availability_polls' }, invalidatePolls)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_poll_responses' }, invalidatePolls)
      .subscribe();

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const respondMutation = useMutation({
    mutationFn: async ({ pollId, response }: { pollId: string; response: PollResponse }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('group_poll_responses')
        .upsert({ poll_id: pollId, user_id: user.id, response }, { onConflict: 'poll_id, user_id' });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      toast({
        title: t('hooks.polls.respondedTitle'),
        description: t('hooks.polls.respondedDesc', {
          response: t(`hooks.polls.response.${variables.response}`),
        }),
      });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    },
    onError: (err: any) => {
      const msg = String(err?.message || '');
      if (msg.includes('match_limit_reached')) {
        toast({
          title: 'Free-tier limit reached',
          description: "You've played your 50 free matches. Upgrade to Premium to vote available.",
          variant: 'destructive',
        });
        return;
      }
      toast({ title: t('hooks.polls.errorRespondTitle'), description: t('hooks.polls.errorRespondDesc'), variant: 'destructive' });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (pollId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('group_poll_responses')
        .upsert({ poll_id: pollId, user_id: user.id, response: 'unavailable' as PollResponse }, { onConflict: 'poll_id, user_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: t('hooks.polls.withdrawnTitle'), description: t('hooks.polls.withdrawnDesc') });
      queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] });
    },
    onError: () => {
      toast({ title: t('hooks.polls.errorWithdrawTitle'), description: t('hooks.polls.errorWithdrawDesc'), variant: 'destructive' });
    },
  });

  return {
    polls,
    loading,
    error: queryError ? (queryError as Error).message : null,
    fetchActivePolls: () => queryClient.invalidateQueries({ queryKey: [POLLS_QUERY_KEY] }),
    respondToPoll: (pollId: string, response: PollResponse) =>
      respondMutation.mutateAsync({ pollId, response }).then(() => true).catch(() => false),
    withdrawResponse: (pollId: string) =>
      withdrawMutation.mutateAsync(pollId).then(() => true).catch(() => false),
  };
};
